import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { formatDate } from '../../utils/dateUtils'

const STATUS_BADGES = {
  new: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  contacted: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  qualified: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  converted: 'bg-green-50 text-green-700 ring-green-600/20',
  lost: 'bg-red-50 text-red-700 ring-red-600/20',
}

function CapturedByBadge({ lead }) {
  // "Bot" when the automated lead-capture flow created the lead;
  // "<agent name>" when a handover agent added it manually.
  if (lead.captured_by === 'agent' && lead.created_by_agent) {
    const label =
      lead.created_by_agent.name ||
      lead.created_by_agent.agent_display_id ||
      'Agent'
    return (
      <span
        title={`Captured by ${label}`}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-indigo-50 text-indigo-700 ring-indigo-600/20 max-w-[180px] truncate"
      >
        <span>👤</span>
        <span className="truncate">{label}</span>
      </span>
    )
  }
  return (
    <span
      title="Captured by the chatbot"
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-slate-100 text-slate-700 ring-slate-600/20"
    >
      <span>🤖</span>
      <span>Bot</span>
    </span>
  )
}

export default function LeadTable({ leads, pagination, onPageChange, onRowClick, loading }) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <div className="w-8 h-8 border-3 border-slate-200 border-t-brand-600 rounded-full animate-spin mx-auto" />
        <p className="text-sm text-slate-500 mt-3">Loading leads...</p>
      </div>
    )
  }

  if (!leads || leads.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <p className="text-sm text-slate-500">No leads found</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell">Company</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">Captured by</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {leads.map((lead) => (
              <tr
                key={lead.id}
                onClick={() => onRowClick(lead)}
                className="hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-sm font-medium text-slate-900 whitespace-nowrap">
                  {lead.name || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                  {lead.email || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap hidden sm:table-cell">
                  {lead.company || '—'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${STATUS_BADGES[lead.status] || STATUS_BADGES.new}`}>
                    {lead.status || 'new'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                  <CapturedByBadge lead={lead} />
                </td>
                <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap hidden lg:table-cell">
                  {formatDate(lead.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-xs text-slate-500">
            Page {pagination.page} of {pagination.total_pages} ({pagination.total} leads)
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-1 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages}
              className="p-1 rounded-lg hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
